import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import api from '../../config/axiosConfig';
import { getItems } from '../../services/itemService';
import { createPurchaseOrder, updatePurchaseOrder } from '../../services/purchaseOrderService';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import { RootState } from '../../store/store';

interface POFormProps {
  onClose: () => void;
  onSuccess: () => void;
  editData?: any;
}

interface POItem {
  item_id: number;
  item_name?: string;
  item_sku?: string;
  qty: number;
  unit_price: number;
  searchQuery?: string;
}

const CreatePOForm: React.FC<POFormProps> = ({ onClose, onSuccess, editData }) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState<number | null>(null);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);

  // Auto-set branch_id based on user role
  const [formData, setFormData] = useState({
    supplier_id: editData?.supplier_id || '',
    // Admin: auto-use their branch, Owner: default to first branch or editData
    branch_id: currentUser?.role === 'owner' 
      ? (editData?.branch_id || 1)
      : (currentUser?.branch_id || 1),
    notes: editData?.notes || '',
  });

  const [poItems, setPOItems] = useState<POItem[]>(
    editData?.items?.map((item: any) => ({
      item_id: item.item_id,
      item_name: item.item_name,
      item_sku: item.item_sku,
      qty: item.qty_ordered || item.qty,
      unit_price: item.unit_price,
      searchQuery: `${item.item_sku} - ${item.item_name}`,
    })) || [{ item_id: 0, qty: 1, unit_price: 0, searchQuery: '' }]
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [suppliersData, branchesData, itemsData] = await Promise.all([
        api.get('/suppliers'),
        api.get('/branches'),
        getItems(),
      ]);
      setSuppliers(suppliersData.data);
      setBranches(branchesData.data);
      setItems(itemsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setPOItems([...poItems, { item_id: 0, qty: 1, unit_price: 0, searchQuery: '' }]);
  };

  const handleRemoveItem = (index: number) => {
    setPOItems(poItems.filter((_, i) => i !== index));
  };

  const handleSearchChange = (index: number, query: string) => {
    const updated = [...poItems];
    updated[index] = { ...updated[index], searchQuery: query, item_id: 0 };
    setPOItems(updated);

    // Filter items based on search query
    if (query.trim().length >= 2) {
      const filtered = items.filter((item) => {
        const searchLower = query.toLowerCase();
        return (
          item.name.toLowerCase().includes(searchLower) ||
          item.sku.toLowerCase().includes(searchLower) ||
          item.id.toString() === query
        );
      }).slice(0, 10); // Limit to 10 suggestions
      setFilteredItems(filtered);
      setShowSuggestions(index);
    } else {
      setFilteredItems([]);
      setShowSuggestions(null);
    }
  };

  const handleSelectItem = (index: number, item: any) => {
    const updated = [...poItems];
    updated[index] = {
      ...updated[index],
      item_id: item.id,
      item_name: item.name,
      item_sku: item.sku,
      searchQuery: `${item.sku} - ${item.name}`,
      unit_price: item.cost_price || updated[index].unit_price,
    };
    setPOItems(updated);
    setShowSuggestions(null);
    setFilteredItems([]);
  };

  const handleItemChange = (index: number, field: keyof POItem, value: any) => {
    const updated = [...poItems];
    updated[index] = { ...updated[index], [field]: value };
    setPOItems(updated);
  };

  const calculateTotal = () => {
    return poItems.reduce((sum, item) => {
      const qty = parseFloat(String(item.qty)) || 0;
      const price = parseFloat(String(item.unit_price)) || 0;
      return sum + qty * price;
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      setError('Please select a supplier');
      return;
    }

    if (!formData.branch_id) {
      setError('Branch tidak ditemukan. Silakan login ulang.');
      return;
    }

    const validItems = poItems.filter(item => item.item_id > 0 && item.qty > 0 && item.unit_price > 0);
    if (validItems.length === 0) {
      setError('Please add at least one valid item');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        supplier_id: parseInt(formData.supplier_id),
        branch_id: parseInt(String(formData.branch_id)),
        notes: formData.notes,
        items: validItems.map(item => ({
          item_id: item.item_id,
          qty: parseFloat(String(item.qty)),
          unit_price: parseFloat(String(item.unit_price)),
        })),
      };

      console.log('Creating PO with payload:', payload);

      if (editData) {
        await updatePurchaseOrder(editData.id, payload);
      } else {
        await createPurchaseOrder(payload);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('PO creation error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  // Get user's branch info for display
  const userBranch = React.useMemo(() => {
    if (!currentUser?.branch_id || !branches.length) return null;
    return branches.find(b => b.id === currentUser.branch_id);
  }, [currentUser, branches]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800">
                {editData ? 'Edit Purchase Order' : 'Buat Purchase Order Baru'}
              </h2>
              
              {/* Branch Info untuk Admin (read-only) */}
              {currentUser?.role !== 'owner' && userBranch && (
                <div className="mt-2 flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 inline-flex">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="font-medium text-blue-800">
                    Cabang: {userBranch.code} - {userBranch.name}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold ml-4"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && <ErrorMessage message={error} />}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Supplier */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.supplier_id}
                onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
                disabled={editData && editData.status !== 'draft'}
              >
                <option value="">Pilih Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Branch - Only for Owner */}
            {currentUser?.role === 'owner' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cabang Tujuan <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData({ ...formData, branch_id: parseInt(e.target.value) })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.code} - {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div className={currentUser?.role !== 'owner' ? 'md:col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Optional notes"
                disabled={editData && editData.status !== 'draft'}
              />
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Items <span className="text-red-500">*</span>
              </label>
              {(!editData || editData.status === 'draft') && (
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                >
                  + Add Item
                </button>
              )}
            </div>

            <div className="space-y-3">
              {poItems.map((item, index) => (
                <div key={index} className="flex gap-3 items-start bg-gray-50 p-3 rounded-lg relative">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={item.searchQuery || ''}
                      onChange={(e) => handleSearchChange(index, e.target.value)}
                      onFocus={() => {
                        if (item.searchQuery && item.searchQuery.length >= 2) {
                          handleSearchChange(index, item.searchQuery);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowSuggestions(null), 200);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Cari nama, SKU, atau ID barang..."
                      required={!item.item_id}
                      disabled={editData && editData.status !== 'draft'}
                    />
                    
                    {/* Autocomplete Suggestions */}
                    {showSuggestions === index && filteredItems.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredItems.map((suggestedItem) => (
                          <button
                            key={suggestedItem.id}
                            type="button"
                            onClick={() => handleSelectItem(index, suggestedItem)}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium text-gray-900">{suggestedItem.name}</div>
                                <div className="text-sm text-gray-500">
                                  SKU: {suggestedItem.sku} | ID: {suggestedItem.id}
                                </div>
                              </div>
                              {suggestedItem.cost_price && (
                                <div className="text-sm text-blue-600 font-medium">
                                  {new Intl.NumberFormat('id-ID', {
                                    style: 'currency',
                                    currency: 'IDR',
                                    minimumFractionDigits: 0,
                                  }).format(suggestedItem.cost_price)}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Selected Item Info */}
                    {item.item_id > 0 && (
                      <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Terpilih: {item.item_sku} - {item.item_name}
                      </div>
                    )}
                  </div>

                  <div className="w-24">
                    <input
                      type="number"
                      value={item.qty}
                      onChange={(e) => handleItemChange(index, 'qty', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Qty"
                      min="1"
                      required
                      disabled={editData && editData.status !== 'draft'}
                    />
                  </div>

                  <div className="w-32">
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Price"
                      min="0"
                      step="0.01"
                      required
                      disabled={editData && editData.status !== 'draft'}
                    />
                  </div>

                  <div className="w-32 flex items-center text-sm text-gray-600">
                    {new Intl.NumberFormat('id-ID', {
                      style: 'currency',
                      currency: 'IDR',
                      minimumFractionDigits: 0,
                    }).format(item.qty * item.unit_price)}
                  </div>

                  {(!editData || editData.status === 'draft') && poItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="text-red-500 hover:text-red-700 font-bold text-xl"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mb-6">
            <div className="flex justify-end items-center text-lg font-bold">
              <span className="text-gray-700 mr-4">Total:</span>
              <span className="text-blue-600">
                {new Intl.NumberFormat('id-ID', {
                  style: 'currency',
                  currency: 'IDR',
                  minimumFractionDigits: 0,
                }).format(calculateTotal())}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
              disabled={submitting || (editData && editData.status !== 'draft')}
            >
              {submitting ? 'Saving...' : editData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePOForm;
